import { useState, useEffect } from 'react';
import { Offcanvas, Button, Form } from 'react-bootstrap';
import axios from 'axios';

const ProformaInvoicePreview = ({
    showModal,
    handleClose,
    createdInvoice,
    quotationData,
    customIntegration,
    setCustomIntegration,
    editableDeploymentIntegration,
    setEditableDeploymentIntegration,
    customIntegrationDescription,
    setCustomIntegrationDescription,
    onDownloadPDF
}) => {
    console.log("🔍 DEBUG ProformaInvoicePreview:");
    console.log("showModal:", showModal);
    console.log("createdInvoice:", createdInvoice);
    console.log("quotationData:", quotationData);

    const [companyDetails, setCompanyDetails] = useState(null);
    const [proformaInvoiceTerms, setProformaInvoiceTerms] = useState('');
    const [bankAccounts, setBankAccounts] = useState([]);

    // Fix: Convert deployment descriptions object to string or array
    const formatDeploymentDescriptions = (descriptions) => {
        if (!descriptions) return '-';

        if (typeof descriptions === 'string') {
            try {
                descriptions = JSON.parse(descriptions);
            } catch (e) {
                return descriptions; // Return as-is if it's already a string
            }
        }

        if (typeof descriptions === 'object') {
            // If it's an object with keys like {136: "Integration with voice agents"}
            if (Object.keys(descriptions).length > 0) {
                return Object.values(descriptions).join(', ');
            }
            return '-';
        }

        return descriptions || '-';
    };

    // Fix: Parse custom integration description HTML safely
    const parseCustomIntegrationDescription = (description) => {
        if (!description) return '-';

        if (typeof description === 'string' && description.startsWith('<')) {
            // Remove HTML tags and convert to plain text
            return description.replace(/<[^>]*>/g, '');
        }

        return description;
    };

    useEffect(() => {
        if (showModal && createdInvoice) {
            const clientCurrency = quotationData?.project?.client?.currency || 'AED';
            fetchCompanyDetails();
            fetchPITerms(clientCurrency);
            fetchBankAccounts();
        }
    }, [showModal, createdInvoice]);

    const fetchPITerms = async (currency) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/quotationterm`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            const term = response.data.quotation_terms.data.find(term => term.type === "proforma_invoice");
            let formattedTerms = "";

            if (term) {
                if (currency === 'AED') {
                    formattedTerms = term.value_local || term.value || "";
                    console.log('📝 Using UAE Local Client Terms (AED) for Proforma Invoice');
                } else if (currency === 'USD') {
                    formattedTerms = term.value_export || term.value || "";
                    console.log('📝 Using Export Client Terms (USD) for Proforma Invoice');
                } else {
                    formattedTerms = term.value || "";
                    console.log('📝 Using Default Terms for Proforma Invoice');
                }

                formattedTerms = formattedTerms.replace(/{{currency}}/g, currency || "USD");
            }
            setProformaInvoiceTerms(formattedTerms);

        } catch (error) {
            console.error("Error fetching quotation terms:", error);
        }
    };

    const fetchCompanyDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/settings`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setCompanyDetails(response.data.setting);
        } catch (error) {
            console.error("Error fetching company details:", error);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/bank-accounts?type=bank account`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
                skipLoader: true,
            });

            const firstTwoAccounts = (response.data.account || []).slice(0, 2);
            setBankAccounts(firstTwoAccounts);
        } catch (error) {
            console.error("Error fetching bank accounts:", error);
            setBankAccounts([]);
        }
    };

    if (!showModal || !createdInvoice) {
        console.log("❌ Preview modal not rendering because:", {
            showModal,
            hasCreatedInvoice: !!createdInvoice
        });
        return null;
    }

    const clientCurrency = quotationData?.project?.client?.currency || 'AED';
    const vatRate = clientCurrency === 'AED' ? 0.05 : 0;

    // Calculate amounts similar to PDF
    const safeNumber = (val) => Number(val) || 0;

    const afterDiscount =
        safeNumber(createdInvoice.basic_amount) +
        safeNumber(createdInvoice.vat) -
        safeNumber(createdInvoice.discount_amount);


    // Number to words function
    const numberToWords = (number) => {
        const words = [
            'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
            'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
            'seventeen', 'eighteen', 'nineteen'
        ];

        const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

        if (number < 20) return words[number];
        if (number < 100) {
            return tens[Math.floor(number / 10)] + (number % 10 ? '-' + words[number % 10] : '');
        }
        if (number < 1000) {
            return words[Math.floor(number / 100)] + ' hundred' + (number % 100 ? ' and ' + numberToWords(number % 100) : '');
        }
        return number.toString();
    };

    // Generate amount in words
    const generateAmountInWords = (amount) => {
        const integerPart = Math.floor(amount);
        const decimalPart = Math.round((amount - integerPart) * 100);

        let words = numberToWords(integerPart);
        words = words.charAt(0).toUpperCase() + words.slice(1);

        if (decimalPart > 0) {
            words += ' and ' + numberToWords(decimalPart) + ' cents';
        }

        const currencyLabel = clientCurrency === 'AED' ? 'UAE' : 'USD';
        return `${currencyLabel} ${words}${decimalPart > 0 ? ` and ${decimalPart}/100` : ''} only.`;
    };

    // Filter bank accounts by currency
    const matchedBanks = bankAccounts.filter(bank =>
        bank.currency?.currency?.toUpperCase() === clientCurrency?.toUpperCase()
    );
    // Helper function to format deployment descriptions as bullet list
    const formatDeploymentDescriptionsAsList = (descriptions) => {
        if (!descriptions) return null;

        if (typeof descriptions === 'string') {
            try {
                descriptions = JSON.parse(descriptions);
            } catch (e) {
                // If it's already a string with HTML list, return as is
                if (descriptions.includes('<ul>') || descriptions.includes('<li>')) {
                    return <div dangerouslySetInnerHTML={{ __html: descriptions }} />;
                }
                // If it's a plain string, convert to list
                return (
                    <ul style={{ margin: 0, paddingLeft: '15px' }}>
                        <li>{descriptions}</li>
                    </ul>
                );
            }
        }

        if (typeof descriptions === 'object') {
            if (Object.keys(descriptions).length > 0) {
                return (
                    <ul style={{ margin: 0, paddingLeft: '15px' }}>
                        {Object.values(descriptions).map((desc, index) => (
                            <li key={index}>{desc}</li>
                        ))}
                    </ul>
                );
            }
            return null;
        }

        return null;
    };

    // Helper function to parse custom integration description as list
    const parseCustomIntegrationDescriptionAsList = (description) => {
        if (!description) return null;

        if (typeof description === 'string') {
            // If it already contains HTML list tags, render as HTML
            if (description.includes('<ul>') || description.includes('<li>') || description.includes('<ol>')) {
                return <div dangerouslySetInnerHTML={{ __html: description }} />;
            }
            // If it's a plain string, assume each line is a list item
            const lines = description.split('\n').filter(line => line.trim());
            if (lines.length > 1) {
                return (
                    <ul style={{ margin: 0, paddingLeft: '15px' }}>
                        {lines.map((line, index) => (
                            <li key={index}>{line.trim()}</li>
                        ))}
                    </ul>
                );
            } else {
                // Single line - show as regular paragraph
                return <div>{description}</div>;
            }
        }

        return <div>{description}</div>;
    };

    return (
        <Offcanvas show={showModal} onHide={handleClose} placement="end" style={{ width: '890px' }}>
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title className="fw-bold">Proforma Invoice Preview</Offcanvas.Title>
                <div className="position-absolute top-0 end-0 p-3 d-flex gap-2 me-4">

                    <Button
                        variant="light"
                        onClick={onDownloadPDF}

                        size="sm"
                        className="shadow-sm"
                        title="Download PDF"
                    >
                        <i className="fa fa-download"></i>
                    </Button>
                </div>
            </Offcanvas.Header>

            <Offcanvas.Body className="p-4" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <div className="preview-container" style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>

                    {/* Header Section */}
                    <div className="header" style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', marginBottom: '20px' }}>
                        <div className="header-left">
                            <h2 className="company-name" style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '6px' }}>
                                {companyDetails?.company_name || 'Systech Technologies'}
                            </h2>
                            <p className="company-details" style={{ fontSize: '12px', lineHeight: '1.5', margin: 0 }}>
                                {companyDetails?.company_address ? (
                                    <div dangerouslySetInnerHTML={{ __html: companyDetails.company_address }} />
                                ) : (
                                    <>
                                        Dar Al Nahda Building, Office 307<br />
                                        P.O Box 91607
                                    </>
                                )}
                                {companyDetails?.phone && <>{companyDetails.phone}<br /></>}
                                {companyDetails?.email && <>{companyDetails.email}<br /></>}
                            </p>
                        </div>
                        <div className="header-right" style={{ position: 'absolute', right: 0, top: 0, textAlign: 'right' }}>
                            {companyDetails?.logo && (
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}/storage/${companyDetails.logo}`}
                                    style={{ maxWidth: '180px', maxHeight: '80px', marginBottom: '10px' }}
                                    alt="Company Logo"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            )}<br/>
                            {/* <div className="quotation-title" style={{ fontSize: '24px', fontWeight: 'bold', color: 'black', marginTop: '12px' }}>
                                Proforma Invoice
                            </div> */}
                        </div>
                    </div>

                    <div className="divider" style={{ borderTop: '1px solid #ddd', margin: '10px 0 20px 0' }}></div>

                    {/* Invoice Details */}
                    <div className="details-container" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', width: '100%' }}>
                        <div className="left-column" style={{ float: 'left', width: '48%' }}>
                            <div className="project-info">
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Project:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {quotationData?.project?.name || 'N/A'}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Document No:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {createdInvoice.ref_no || 'N/A'}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Date:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {new Date(
                                            createdInvoice.invoice_date ?? createdInvoice.created_at
                                        ).toLocaleDateString('en-GB')}
                                    </span>

                                </div>
                            </div>
                        </div>
                        <div className="right-column" style={{ float: 'right', width: '48%' }}>
                            <div className="customer-info">
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Customer:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {quotationData?.project?.client?.name || ''}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Address:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {quotationData?.project?.client?.address || ''}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Contact Phone:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {quotationData?.project?.client?.contacts?.[0]?.phone || ''}
                                    </span>
                                </div>
                                <div className="detail-row" style={{ marginBottom: '8px' }}>
                                    <strong className="detail-label" style={{ display: 'inline-block', width: '100px', fontWeight: 'bold' }}>Contact Email:</strong>
                                    <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                        {quotationData?.project?.client?.contacts?.[0]?.email || ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Products Table with Deployment Details */}
                    {/* <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '11px' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Description</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>QTY</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Rate</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Amount</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>{vatRate * 100}% VAT</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Amount Inc. of VAT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotationData?.product_item_details?.map((item, index) => {
                                const subtotal = Number(item.subtotal) || 0;
                                const vatAmount = Number(subtotal * vatRate);
                                const totalAmount = Number(subtotal + vatAmount);

                                return (
                                    <tr key={index}>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                            {item.product?.description || item.item_description || 'N/A'}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {item.quantity || ''}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {Number(item.price || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {subtotal.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {vatAmount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {totalAmount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                );
                            })}
                            {quotationData?.service_amount > 0 && (
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                        <strong>One Time onboarding cost. Configuration & Testing and User training</strong>
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>1</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                        {Number(quotationData.service_amount).toLocaleString('en-AE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                        {Number(quotationData.service_amount).toLocaleString('en-AE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                        {(Number(quotationData.service_amount) * vatRate).toLocaleString('en-AE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                        {(Number(quotationData.service_amount) * (1 + vatRate)).toLocaleString('en-AE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                </tr>
                            )}

                            {customIntegration && (
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontStyle: 'italic' }}>
                                        <strong>Custom Integration:</strong> {customIntegration}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                </tr>
                            )}

                            {(customIntegration || editableDeploymentIntegration || customIntegrationDescription) && (() => {
                                const deploymentAmount = Number(quotationData?.deplyment_amount || 0);
                                const vatRate = clientCurrency === 'AED' ? 0.05 : 0;
                                const vatAmount = deploymentAmount * vatRate;
                                const totalWithVat = deploymentAmount + vatAmount;

                                return (
                                    <tr>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                                Custom Integration
                                            </div>

                                            <div style={{ paddingLeft: '15px' }}>
                                                {editableDeploymentIntegration && Object.keys(editableDeploymentIntegration).length > 0 && (
                                                    <div style={{ marginBottom: '6px' }}>
                                                        {formatDeploymentDescriptionsAsList(editableDeploymentIntegration)}
                                                    </div>
                                                )}

                                                {customIntegrationDescription && (
                                                    <div>
                                                        {parseCustomIntegrationDescriptionAsList(customIntegrationDescription)}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>1</td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {deploymentAmount.toLocaleString('en-AE', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {deploymentAmount.toLocaleString('en-AE', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {vatAmount.toLocaleString('en-AE', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {totalWithVat.toLocaleString('en-AE', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                    </tr>
                                );
                            })()}

                            {(!quotationData?.product_item_details || quotationData.product_item_details.length === 0) && (
                                <tr>
                                    <td colSpan="6" style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontStyle: 'italic' }}>
                                        No items available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            {(() => {
                                // Product totals
                                const productTotals = quotationData?.product_item_details?.reduce((acc, item) => {
                                    const subtotal = Number(item.subtotal) || 0;
                                    const vatAmount = subtotal * vatRate;
                                    const totalAmount = subtotal + vatAmount;

                                    return acc + totalAmount;
                                }, 0) || 0;

                                // Deployment totals
                                const deploymentAmount = Number(quotationData?.deplyment_amount || 0);
                                const deploymentVat = deploymentAmount * vatRate;
                                const deploymentTotal = deploymentAmount + deploymentVat;

                                // Final total
                                const finalTotal = productTotals + deploymentTotal;

                                return (
                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                        <td colSpan="5" style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }} className='text-end'>
                                            Total
                                        </td>

                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                                            {finalTotal.toLocaleString('en-AE', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                        </td>
                                    </tr>
                                );
                            })()}
                        </tfoot>

                    </table> */}

                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '11px' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Description</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>QTY</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Rate</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Amount</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>{vatRate * 100}% VAT</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Amount Inc. of VAT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Original Product Items */}
                            {quotationData?.product_item_details?.map((item, index) => {
                                const subtotal = Number(item.subtotal) || 0;
                                const vatAmount = Number(subtotal * vatRate);
                                const totalAmount = Number(subtotal + vatAmount);

                                return (
                                    <tr key={index}>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                            {/* {item.product?.description || item.item_description || 'N/A'} */}
                                            <strong>{item.product?.product_name} :</strong>  {item.product?.description || item.item_description || ''}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {item.quantity || ''}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {Number(item.price || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {subtotal.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {vatAmount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                            {totalAmount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                );
                            })}

                            {/* Service Amount Row - Only show if service_amount has value */}
                            {quotationData?.service_amount > 0 && (
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                        <strong>One Time onboarding cost. Configuration & Testing and User training</strong>
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>1</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                        {Number(quotationData.service_amount).toLocaleString('en-AE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                        {Number(quotationData.service_amount).toLocaleString('en-AE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                        {(Number(quotationData.service_amount) * vatRate).toLocaleString('en-AE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                        {(Number(quotationData.service_amount) * (1 + vatRate)).toLocaleString('en-AE', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                </tr>
                            )}

                            {/* Custom Integration Description Row - Only show if has value */}
                            {customIntegration && (
                                <tr>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontStyle: 'italic' }}>
                                        <strong>Custom Integration:</strong> {customIntegration}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>-</td>
                                </tr>
                            )}

                            {/* Combined Descriptions Row - Only show if any description exists */}
                            {(customIntegration || editableDeploymentIntegration || customIntegrationDescription) && (() => {
                                // Calculate deployment amount by subtracting service_amount
                                const baseDeploymentAmount = Number(quotationData?.deplyment_amount || 0);
                                const serviceAmount = Number(quotationData?.service_amount || 0);
                                const deploymentAmount = Math.max(0, baseDeploymentAmount - serviceAmount); // Ensure non-negative

                                const vatRate = clientCurrency === 'AED' ? 0.05 : 0;
                                const vatAmount = deploymentAmount * vatRate;
                                const totalWithVat = deploymentAmount + vatAmount;

                                // Only show if there's an actual deployment amount after subtraction
                                if (deploymentAmount > 0) {
                                    return (
                                        <tr>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                                    Custom Integration
                                                </div>

                                                <div style={{ paddingLeft: '15px' }}>
                                                    {/* Deployment Integration as list items */}
                                                    {editableDeploymentIntegration && Object.keys(editableDeploymentIntegration).length > 0 && (
                                                        <div style={{ marginBottom: '6px' }}>
                                                            {formatDeploymentDescriptionsAsList(editableDeploymentIntegration)}
                                                        </div>
                                                    )}

                                                    {/* Custom Integration Description as list items */}
                                                    {customIntegrationDescription && (
                                                        <div>
                                                            {parseCustomIntegrationDescriptionAsList(customIntegrationDescription)}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>1</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                {deploymentAmount.toLocaleString('en-AE', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                {deploymentAmount.toLocaleString('en-AE', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                {vatAmount.toLocaleString('en-AE', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                {totalWithVat.toLocaleString('en-AE', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </td>
                                        </tr>
                                    );
                                }
                                return null;
                            })()}

                            {(!quotationData?.product_item_details || quotationData.product_item_details.length === 0) && (
                                <tr>
                                    <td colSpan="6" style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontStyle: 'italic' }}>
                                        No items available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            {(() => {
                                // Product totals
                                const productTotals = quotationData?.product_item_details?.reduce((acc, item) => {
                                    const subtotal = Number(item.subtotal) || 0;
                                    const vatAmount = subtotal * vatRate;
                                    const totalAmount = subtotal + vatAmount;
                                    return acc + totalAmount;
                                }, 0) || 0;

                                // Service amount totals
                                const serviceAmount = Number(quotationData?.service_amount || 0);
                                const serviceVat = serviceAmount * vatRate;
                                const serviceTotal = serviceAmount + serviceVat;

                                // Deployment totals (after subtracting service_amount)
                                const baseDeploymentAmount = Number(quotationData?.deplyment_amount || 0);
                                const deploymentAmount = Math.max(0, baseDeploymentAmount - serviceAmount);
                                const deploymentVat = deploymentAmount * vatRate;
                                const deploymentTotal = deploymentAmount + deploymentVat;

                                // Final total
                                const finalTotal = productTotals + serviceTotal + deploymentTotal;

                                return (
                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                        {/* Label */}
                                        <td colSpan="5" style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }} className='text-end'>
                                            Total
                                        </td>

                                        {/* Amount */}
                                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                                            {finalTotal.toLocaleString('en-AE', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                        </td>
                                    </tr>
                                );
                            })()}
                        </tfoot>
                    </table>

                    {/* Amount Summary */}
                    <div style={{ width: '250px', marginLeft: 'auto', fontSize: '11px', marginTop: '10px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: 'none' }}>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', width: '60%', border: 'none' }}>
                                    {Number(createdInvoice.advanced_amount || 0).toLocaleString('en-AE')}% Invoiced Amount:
                                </td>
                                <td style={{ padding: '4px 0', textAlign: 'right', border: 'none' }}>
                                    {Number(createdInvoice.basic_amount || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>
                                    {createdInvoice.vat > 0 ? 'Invoiced VAT:' : 'Total VAT:'}
                                </td>
                                <td style={{ padding: '4px 0', textAlign: 'right', border: 'none' }}>
                                    {Number(createdInvoice.vat || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                            </tr>
                            {createdInvoice.discount_amount && createdInvoice.discount_amount > 0 && (
                                <tr>
                                    <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>
                                        Discount{createdInvoice.quotation?.discount_type === 'percentage' ? ' (%)' : ''}:
                                    </td>
                                    <td style={{ padding: '4px 0', textAlign: 'right', border: 'none' }}>
                                        -{Number(createdInvoice.discount_amount || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>Total:</td>
                                <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 'bold', border: 'none' }}>
                                    {afterDiscount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                            </tr>
                        </table>
                    </div>

                    {/* Amount in Words */}
                    <div style={{
                        marginTop: '20px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '10px 15px',
                        background: '#fafafa',
                        fontSize: '13px',
                        color: '#000'
                    }}>
                        <strong>Amount (in Words):</strong> {generateAmountInWords(afterDiscount)}
                    </div>

                    {/* Terms & Conditions */}
                    {proformaInvoiceTerms && (
                        <div style={{ marginTop: '20px', padding: '10px 15px' }}>
                            <div style={{ fontSize: '12px', color: '#000', fontWeight: 'bold' }}>
                                Terms & Conditions:
                            </div>
                            <div
                                style={{ fontSize: '11px', lineHeight: '1.6', color: '#000', padding: '12px 18px' }}
                                dangerouslySetInnerHTML={{ __html: proformaInvoiceTerms }}
                            />
                        </div>
                    )}

                    {/* Bank Details */}
                    {matchedBanks.length > 0 && (
                        <div className="bank-details" style={{
                            marginTop: '20px',
                            border: '1px solid #ccc',
                            borderRadius: '6px',
                            padding: '12px',
                            background: '#fff'
                        }}>
                            <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '13px',
                                color: '#000',
                                borderBottom: '1px solid #003366',
                                paddingBottom: '5px'
                            }}>
                                Bank Details
                            </h4>

                            {matchedBanks.length === 1 ? (
                                <table width="100%" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Account Name:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].account_holder_name}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Bank Name:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].name}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Account No:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].account_number}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>IBAN:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].iban}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>SWIFT:</td>
                                        <td style={{ padding: '4px 0' }}>{matchedBanks[0].swift_code}</td>
                                    </tr>
                                </table>
                            ) : (
                                <table width="100%" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <tr>
                                        {matchedBanks.map((bank, index) => (
                                            <td key={index} style={{ verticalAlign: 'top', padding: '0 15px', borderRight: index < matchedBanks.length - 1 ? '1px solid #ccc' : 'none' }}>
                                                <table width="100%" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>Account Name:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.account_holder_name}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>Bank Name:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.name}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>Account No:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.account_number}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>IBAN:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.iban}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '3px 0', fontWeight: 'bold' }}>SWIFT:</td>
                                                        <td style={{ padding: '3px 0' }}>{bank.swift_code}</td>
                                                    </tr>
                                                </table>
                                            </td>
                                        ))}
                                    </tr>
                                </table>
                            )}
                        </div>
                    )}

                </div>
            </Offcanvas.Body>

            <div className="p-4 d-flex justify-content-end border-top bg-light">
                <Button
                    variant="outline-secondary"
                    onClick={handleClose}
                    className="me-2"
                    size="sm"
                >
                    Close
                </Button>


            </div>

            <style jsx>{`
                @media print {
                    .p-4, .border-top, .bg-light {
                        display: none !important;
                    }
                    
                    .preview-container {
                        margin: 0;
                        padding: 0;
                    }
                    
                    body {
                        width: 190mm;
                        min-height: 200mm;
                        margin: 0 auto;
                        padding: 10mm;
                        font-size: 12px;
                        background-color: #ffffff;
                        border: 1px solid #ddd;
                        border-radius: 12px;
                    }
                }
            `}</style>
        </Offcanvas>
    );
};

export default ProformaInvoicePreview;
